import { MAIN_STATUSES } from './constants';
import { type Service } from './Service';
import { type Shipment } from './Shipment';

interface ShippingAddress {
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  postalCode: string;
  nickname: string;
  type: string;
  reference: string;
  storeName: string;
}

interface Installments {
  quantity: number;
  value: number;
  url: string;
}

interface Payment {
  mode: string;
  type: string;
  value: number;
  installments: Installments;
  retryUrl: string;
  url: string;
  hasInterestRate: boolean;
  brand: string;
}

interface Values {
  total: number;
  subTotal: number;
  shippingCost: number;
  discount: number;
  vouchers?: number;
}

export type InstallationAddress = ShippingAddress;

interface Services {
  items: Service[];
  installationAddress: InstallationAddress;
}

export interface SaleOrderData {
  id: string;
  createdAt: string;
  status: string;
  shipments: Shipment[];
  payments: Payment[];
  values: Values;
  phone: string;
  services: Services | null;
  recipient: string;
  // issuerPlant: string;
}

export class SaleOrder {
  id;
  createdAt;
  status;
  shipments;
  values;
  phone;
  services;
  recipient;
  payments;
  paymentMethodSlug;
  paymentMethodName;
  isCreditCardPayment;
  retryPaymentUrl;
  deliveryShipments;
  withdrawalShipments;
  isPendingPayment;
  sortedShipments;

  constructor(data: SaleOrderData) {
    this.id = data.id;
    this.createdAt = data.createdAt;
    this.status = data.status;
    this.shipments = data.shipments;
    this.values = data.values;
    this.phone = data.phone;
    this.services = data.services;
    this.recipient = data.recipient;
    this.payments = data.payments;
    this.paymentMethodSlug = this.#buildPaymentMethodSlug();
    this.paymentMethodName = this.#buildPaymentMethodName();
    this.isCreditCardPayment = this.#buildIsCreditCardPayment();
    this.retryPaymentUrl = this.#buildRetryPaymentUrl();
    this.deliveryShipments = this.#buildDeliveryShipments();
    this.withdrawalShipments = this.#buildWithdrawalShipments();
    this.isPendingPayment = this.#buildIsPendingPayment();
    this.sortedShipments = this.#buildSortedShipments();
  }

  #buildPaymentMethodSlug() {
    return this.payments[0].type;
  }

  #buildPaymentMethodName() {
    return this.payments[0].mode;
  }

  #buildIsCreditCardPayment() {
    return this.paymentMethodSlug === 'credit_card';
  }

  #buildRetryPaymentUrl() {
    return this.payments.find((payment) => payment.retryUrl)?.retryUrl;
  }

  #buildDeliveryShipments() {
    return this.shipments.filter((shipment) => shipment.isDelivery);
  }

  #buildWithdrawalShipments() {
    return this.shipments.filter((shipment) => shipment.isWithdrawal);
  }

  #buildIsPendingPayment() {
    return this.status === MAIN_STATUSES.PENDING_PAYMENT;
  }

  #buildSortedShipments() {
    return [...this.deliveryShipments, ...this.withdrawalShipments];
  }
}
